import { useLayoutEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { useAuth } from '@/context/AuthContext';
import { getActiveSessionRevision, subscribeActiveSessionRevision } from '@/api/authenticatedClient';
import { isDesktopOffline } from '@/api/desktopSync';
import { getProducts, getProduct } from '@/api/products';
import { getSources, getSendingFlorists } from '@/api/sources';

const principal = binding => binding?.user?.id && binding?.user?.tenant_id && binding.source === 'cloud'
  ? JSON.stringify([binding.user.tenant_id, binding.user.id]) : null;
const connection = () => JSON.stringify([window.lvbDesktop?.backendUrl, window.lvbDesktop?.localBackendUrl]);
// These in-memory obligations outlive a breakout or panel. They are not durable
// receipts, and retiring a screen never cancels a dispatched server mutation.
const writes = new Map();
const epochs = new Map();
const observers = new Set();
const publishWrites = (scope, origin, settled = false) => observers.forEach(notify => notify(scope, origin, settled));
let openingSequence = 0;

export const validProduct = value => !!value && typeof value.id === 'string' && !!value.id
  && typeof value.product_code === 'string' && !!value.owner
  && ['network', 'florist'].includes(value.owner.type) && typeof value.owner.id === 'string'
  && ['product_name', 'notes', 'reference_photo_url'].every(key => value[key] == null || typeof value[key] === 'string')
  && Array.isArray(value.variants) && value.variants.every(variant => variant && typeof variant.size_label === 'string'
    && ['code', 'recipe_text'].every(key => variant[key] == null || typeof variant[key] === 'string')
    && (variant.retail_price == null || Number.isFinite(variant.retail_price)));

function catalogController(authorize, scope) {
  const owner = {};
  let active = false;
  let opening = null;
  let listVersion = 0;
  let ownerVersion = 0;
  let observationVersion = 0;
  const observations = new Map();
  const reads = new Set();
  const listeners = new Set();
  let snapshot = { products: null, owners: null, listState: 'loading', ownerState: 'loading', notice: '', active: false, writes: {} };
  const owns = () => active && authorize() !== null;
  const notify = () => {
    snapshot = { ...snapshot, active: owns(), writes: Object.fromEntries([...writes].filter(([key]) => key.startsWith(scope)).map(([key, value]) => [key.slice(scope.length), value])) };
    listeners.forEach(listener => listener());
  };
  const patch = value => { snapshot = { ...snapshot, ...value }; notify(); };
  const read = async execute => {
    const config = owns() && authorize();
    if (!config) throw new Error('Product session unavailable');
    const controller = new AbortController();
    reads.add(controller);
    try { return await execute({ ...config, signal: controller.signal }); }
    finally { reads.delete(controller); }
  };
  const refreshList = async () => {
    if (!owns()) return false;
    const version = ++listVersion;
    const epoch = epochs.get(scope) || 0;
    const observedAtStart = observationVersion;
    patch({ listState: 'loading' });
    try {
      const products = await read(getProducts);
      if (!Array.isArray(products) || !products.every(validProduct)) throw new Error('Invalid product list');
      if (!owns() || version !== listVersion || epoch !== (epochs.get(scope) || 0)) return false;
      const merged = new Map(products.map(product => [product.id, product]));
      observations.forEach(({ version: observed, product }, id) => {
        if (observed > observedAtStart) merged.set(id, product);
      });
      patch({ products: [...merged.values()], listState: 'available' });
      return true;
    } catch {
      if (owns() && version === listVersion) patch({ listState: 'unavailable' });
      return false;
    }
  };
  const refreshOwners = async () => {
    if (!owns()) return false;
    const version = ++ownerVersion;
    patch({ ownerState: 'loading' });
    try {
      const [sources, florists] = await read(config => Promise.all([getSources(config), getSendingFlorists(config)]));
      const valid = rows => Array.isArray(rows) && rows.every(row => row && typeof row.id === 'string' && typeof row.name === 'string');
      if (!valid(sources) || !valid(florists)) throw new Error('Invalid product owners');
      if (!owns() || version !== ownerVersion) return false;
      const byName = (a, b) => a.name.localeCompare(b.name);
      patch({ ownerState: 'available', owners: [
        ...sources.map(row => ({ type: 'network', id: row.id, name: row.name })).sort(byName),
        ...florists.filter(row => row.owns_catalog).map(row => ({ type: 'florist', id: row.id, name: row.name })).sort(byName),
      ] });
      return true;
    } catch {
      if (owns() && version === ownerVersion) patch({ ownerState: 'unavailable' });
      return false;
    }
  };
  const blocked = id => !owns() || writes.has(scope + id);
  const writesChanged = (changedScope, origin, settled) => {
    if (changedScope !== scope) return;
    if (origin !== owner) {
      ++listVersion;
      if (settled && owns()) refreshList();
    }
    notify();
  };
  const mutation = async (id, execute, validate) => {
    if (blocked(id)) return { kind: 'blocked' };
    const config = authorize();
    if (!config) return { kind: 'blocked' };
    const key = scope + id;
    const attempt = { status: 'pending' };
    writes.set(key, attempt);
    ++listVersion; // A pre-write read cannot publish after this admission.
    epochs.set(scope, (epochs.get(scope) || 0) + 1);
    publishWrites(scope, owner);
    let result;
    try {
      const value = await execute(config);
      result = validate(value) ? { kind: 'accepted', value } : { kind: 'unknown' };
    } catch (error) {
      // Product routes explicitly refuse invalid input, missing targets, forbidden
      // sessions and stale transactions. Transport/5xx may follow a committed write
      // (including post-commit media cleanup), so those never mean rejection.
      const refused = [400, 401, 403, 404, 409, 413, 415, 422].includes(error?.response?.status);
      result = { kind: refused ? 'rejected' : 'unknown' };
    } finally {
      ++listVersion;
      epochs.set(scope, (epochs.get(scope) || 0) + 1);
      if (writes.get(key) === attempt) {
        if (result?.kind === 'unknown') writes.set(key, { status: 'unknown' });
        else writes.delete(key);
      }
      // An invalidated acquisition must not leave a permanent loading label.
      // Callers can follow an acknowledged write with a new read; otherwise the
      // screen retains its prior observation and exposes read-only retry.
      if (owns() && snapshot.listState === 'loading') patch({ listState: 'unavailable' });
      publishWrites(scope, owner, true);
    }
    if (!owns()) return { kind: 'retired' };
    return result;
  };
  return {
    subscribe: listener => { listeners.add(listener); return () => listeners.delete(listener); },
    getSnapshot: () => snapshot,
    activate() { active = true; observers.add(writesChanged); notify(); refreshList(); refreshOwners(); },
    retire() { active = false; opening = null; ++listVersion; ++ownerVersion; reads.forEach(controller => controller.abort()); observers.delete(writesChanged); notify(); },
    owns, blocked, mutation, refreshList, refreshOwners,
    open(productId = null) {
      if (!owns()) return null;
      const next = { key: ++openingSequence, productId, current: () => owns() && opening === next };
      opening = next;
      return next;
    },
    close(current) { if (!current?.current()) return false; opening = null; return true; },
    async readProduct(id) {
      const epoch = epochs.get(scope) || 0;
      const value = await read(config => getProduct(id, config));
      if (!validProduct(value) || value.id !== id) throw new Error('Invalid product');
      if (!owns() || epoch !== (epochs.get(scope) || 0)) throw new Error('Product observation retired');
      return value;
    },
    observeProduct(product) {
      if (!owns()) return;
      observations.set(product.id, { product, version: ++observationVersion });
      const prior = snapshot.products;
      if (!prior) return; // One detail observation is not a complete catalog.
      patch({ products: prior.some(row => row.id === product.id) ? prior.map(row => row.id === product.id ? product : row) : [...prior, product] });
    },
    observeDeleted(id) {
      if (!owns()) return;
      observations.delete(id);
      patch({ products: snapshot.products?.filter(row => row.id !== id) ?? null });
    },
    reviewed(id) {
      if (owns() && writes.get(scope + id)?.status === 'unknown' && id !== '$create') {
        writes.delete(scope + id); publishWrites(scope, owner);
      }
    },
    notice(message) { if (owns()) patch({ notice: message }); },
  };
}

export function useProductCatalog() {
  const { sessionBinding, getToken } = useAuth() || {};
  const revision = useSyncExternalStore(subscribeActiveSessionRevision, getActiveSessionRevision);
  const identity = principal(sessionBinding);
  const source = connection();
  const roles = sessionBinding?.user?.roles || [sessionBinding?.user?.role];
  const permitted = roles.includes('admin') && !isDesktopOffline();
  const latest = useRef(null);
  latest.current = { sessionBinding, getToken, identity, source };
  const controller = useMemo(() => {
    const authorize = () => {
      const current = latest.current;
      const roles = current.sessionBinding?.user?.roles || [current.sessionBinding?.user?.role];
      if (!permitted || !identity || current.identity !== identity || current.source !== source || connection() !== source
        || current.sessionBinding?.revision !== revision || getActiveSessionRevision() !== revision
        || !roles.includes('admin') || isDesktopOffline()) return null;
      const token = current.getToken?.();
      return token ? { headers: { Authorization: `Bearer ${token}` }, timeout: 30000, suppressServerErrorNotification: true } : null;
    };
    return catalogController(authorize, JSON.stringify([identity, revision, source]) + ':');
  }, [identity, revision, source, permitted]);
  useLayoutEffect(() => { controller.activate(); return () => controller.retire(); }, [controller]);
  const snapshot = useProductCatalogSnapshot(controller);
  return { controller, ...snapshot };
}

export function useProductCatalogSnapshot(controller) {
  return useSyncExternalStore(controller.subscribe, controller.getSnapshot);
}
